/**
 * Sales Tasks API
 * Quick follow-up tasks and reminders
 */
import apiClient from './index'

export interface SalesTask {
  id: string
  title: string
  description?: string
  task_type: 'follow_up' | 'call' | 'email' | 'meeting' | 'other'
  due_date?: string
  due_time?: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  status: 'pending' | 'completed' | 'cancelled'
  completed_at?: string
  contact_id?: string
  contact_name?: string
  company_id?: string
  company_name?: string
  deal_id?: string
  deal_name?: string
  assigned_to_user_id?: string
  assigned_to_name?: string
  created_by_user_id?: string
  created_by_name?: string
  reminder_minutes?: number
  created_at: string
  updated_at: string
  // Set when the row is sourced from Microsoft Planner (via include_planner=true),
  // rather than a native CRM SalesTask. Drives badging + disables inline complete.
  source?: 'crm' | 'planner'
  planner_task_id?: string
  planner_plan_id?: string
}

export interface CreateSalesTaskData {
  title: string
  description?: string
  task_type?: 'follow_up' | 'call' | 'email' | 'meeting' | 'other'
  due_date?: string
  due_time?: string
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  contact_id?: string
  contact_name?: string
  company_id?: string
  company_name?: string
  deal_id?: string
  deal_name?: string
  assigned_to_user_id?: string
  assigned_to_name?: string
  reminder_minutes?: number
}

export interface UpdateSalesTaskData extends Partial<CreateSalesTaskData> {}

export interface SalesTaskStats {
  pending: number
  overdue: number
  due_today: number
}

interface SalesTasksResponse {
  data: SalesTask[]
  next_cursor?: string
}

interface SalesTaskResponse {
  data: SalesTask
}

interface SalesTaskStatsResponse {
  data: SalesTaskStats
}

// List tasks
export async function listSalesTasks(params?: {
  status?: string
  contact_id?: string
  deal_id?: string
  assigned_to?: string
  view?: 'today' | 'overdue' | 'upcoming'
  limit?: number
  cursor?: string
  /** Also include the user's Microsoft Planner items (synced via PlannerTask)
   *  that aren't already mirrored as a CRM SalesTask. */
  include_planner?: boolean
}): Promise<SalesTasksResponse> {
  const searchParams = new URLSearchParams()
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, String(value))
    })
  }
  const { data } = await apiClient.get<SalesTasksResponse>(`/sales-tasks?${searchParams}`)
  return data
}

// Create task
export async function createSalesTask(taskData: CreateSalesTaskData): Promise<SalesTask> {
  const { data } = await apiClient.post<SalesTaskResponse>('/sales-tasks', taskData)
  return data.data
}

// Get task
export async function getSalesTask(taskId: string): Promise<SalesTask> {
  const { data } = await apiClient.get<SalesTaskResponse>(`/sales-tasks/${taskId}`)
  return data.data
}

// Update task
export async function updateSalesTask(taskId: string, taskData: UpdateSalesTaskData): Promise<SalesTask> {
  const { data } = await apiClient.put<SalesTaskResponse>(`/sales-tasks/${taskId}`, taskData)
  return data.data
}

// Delete task
export async function deleteSalesTask(taskId: string): Promise<void> {
  await apiClient.delete(`/sales-tasks/${taskId}`)
}

// Complete task
export async function completeSalesTask(taskId: string): Promise<SalesTask> {
  const { data } = await apiClient.post<SalesTaskResponse>(`/sales-tasks/${taskId}/complete`)
  return data.data
}

// Reopen task
export async function reopenSalesTask(taskId: string): Promise<SalesTask> {
  const { data } = await apiClient.post<SalesTaskResponse>(`/sales-tasks/${taskId}/reopen`)
  return data.data
}

// Get stats
export async function getSalesTaskStats(): Promise<SalesTaskStats> {
  const { data } = await apiClient.get<SalesTaskStatsResponse>('/sales-tasks/stats')
  return data.data
}

// Get tasks for contact
export async function getContactSalesTasks(contactId: string, includeCompleted = false): Promise<SalesTask[]> {
  const { data } = await apiClient.get<SalesTasksResponse>(
    `/contacts/${contactId}/sales-tasks?include_completed=${includeCompleted}`
  )
  return data.data
}

// Get tasks for deal
export async function getDealSalesTasks(dealId: string, includeCompleted = false): Promise<SalesTask[]> {
  const { data } = await apiClient.get<SalesTasksResponse>(
    `/deals/${dealId}/sales-tasks?include_completed=${includeCompleted}`
  )
  return data.data
}
